/** @file updateEffectRuns: effect sprites started, restarted and stopped by this update's clip events. */
import type { EffectDrawable } from '@zilo/pose3d-format/types/drawable';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { PuppetEvent } from '@zilo/pose3d-runtime-babylon/clip/clipClockTypes';
import { effectFrameRect } from '@zilo/pose3d-runtime-babylon/effect/effectFrameRect';
import type { EffectRun } from '@zilo/pose3d-runtime-babylon/effect/effectTypes';
import { eventName } from '@zilo/pose3d-runtime-babylon/effect/eventName';

/** Whether `e` stops effect `d`: its name is `trigger.stop`, or it is a `stop`-mode event on the effect's node. */
const stops = (d: EffectDrawable, e: PuppetEvent): boolean => d.trigger.stop === eventName(e) || (e.mode === 'stop' && e.node === d.node);

/** Whether run `r` has played every frame of its one-shot strip by `now` (the rule `effectFrameRect` draws by). */
const finished = (d: EffectDrawable, r: EffectRun, now: number): boolean => effectFrameRect(d.frames, now - r.started) === null;

/**
 * The runs after `events` (in order): a stopping event removes the effect's run; an event named by `trigger.start`
 * (re)starts it at `now`, pinned at the event's world point for mode `fixed`, following the node otherwise. Finished
 * one-shots are dropped. A new array; the input is not changed.
 */
export function updateEffectRuns(runs: readonly EffectRun[], puppet: Puppet, events: readonly PuppetEvent[], now: number): EffectRun[] {
  let next = [...runs];
  for (const e of events) {
    puppet.drawables.forEach((d, i) => {
      if (d.kind !== 'effect') return;
      if (stops(d, e)) next = next.filter((r) => r.drawable !== i);
      else if (d.trigger.start === eventName(e)) {
        next = [...next.filter((r) => r.drawable !== i), { drawable: i, started: now, anchor: e.mode === 'fixed' ? [...e.world] : null }];
      }
    });
  }
  return next.filter((r) => {
    const d = puppet.drawables[r.drawable];
    return d?.kind === 'effect' && !finished(d, r, now);
  });
}
